import React, { useState, useRef, useEffect, useContext } from "react";
import Link from "next/link";
import Image from "next/image";
import Head from "next/head";
import SignatureCanvas from "react-signature-canvas";
import { AiFillCheckCircle, AiOutlineClose } from "react-icons/ai";
import AppContext from "../../../components/AppContext";
import { createUTMQueryParams } from "@utils/signature_owner";

const DrawSignature = () => {
  const context = useContext(AppContext);
  const [penColor, setPenColor] = useState("#000000");
  const [penSize, setPenSize] = useState(3);
  const [signatureUrl, setSignatureUrl] = useState("");
  const [showModal, setShowModal] = useState(false);

  const [screenWidth, setScreenWidth] = useState(
    globalThis.window?.innerWidth > 800
      ? 700
      : globalThis.window?.innerWidth - 50
  );

  useEffect(() => {
    const handleResize = () => {
      if (globalThis.window?.innerWidth > 800) {
        setScreenWidth(700);
      } else {
        setScreenWidth(globalThis.window?.innerWidth - 50);
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const canvasRef = useRef();

  const clear = () => {
    canvasRef.current.clear();
    setSignatureUrl("");
  };

  const openModal = () => {
    if (canvasRef.current.isEmpty()) {
      return;
    }
    const dataURL = canvasRef.current.toDataURL("image/png");
    setSignatureUrl(dataURL);
    setShowModal(true);
  };

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = signatureUrl;
    link.download = "signature.png";
    link.click();
  };

  return (
    <>
      <Head>
        <title>
          BoloForms Signature Tool - Draw & Download Your eSignature
        </title>
        <meta
          name="description"
          content="BoloForms Signature Tool - Create your unique digital signature with ease. Use our advanced features to draw your signature or manually select a signature font. Perfect for documents, contracts, and more."
        />
        <meta
          name="keywords"
          content="draw signature, draw signature online, draw signature online free, draw signature online free download, draw signature online free download png, draw signature online free download transparent, draw signature online free download transparent background, draw signature online free download transparent png, draw signature online free download transparent background, draw signature online free download transparent background png, draw signature online free download transparent background free, draw signature online free download transparent background free png, draw signature online free download transparent background free download, draw signature online free download transparent background free download png, draw signature online free download transparent background free download free, draw signature online free download transparent background free download free png, draw signature online free download transparent background free download free download, draw signature online free download transparent background free download free download png, online signature tool"
        />
        <meta name="generator" content="BoloForms Signature Tool" />
        <meta
          property="og:title"
          content="BoloForms Signature Tool - Draw & Download Your eSignature"
        />
        <meta
          property="og:description"
          content="BoloForms Signature Tool allows you to create your unique digital signature effortlessly. Draw your signature or choose from a selection of signature fonts. Ideal for documents, contracts, and more."
        />
        <meta
          property="og:url"
          content="https://www.bolosign.com/signature/online-signature/draw"
        />
        <meta property="og:type" content="website" />
        <meta
          name="robots"
          content="INDEX, FOLLOW, MAX-IMAGE-PREVIEW:LARGE, MAX-SNIPPET:-1, MAX-VIDEO-PREVIEW:-1"
        />
        <link
          rel="canonical"
          href="https://www.bolosign.com/signature/online-signature/draw"
        />
      </Head>

      <div className="flex flex-col max-w-[1280px] gap-[50px] mx-auto">
        <div className="flex flex-col justify-center items-center text-center">
          {/* <div className="text-[20px] text-violet-800">Create and download your free e-signature</div> */}
          <div className="text-[35px] md:text-[50px] font-bold text-gray-800">
            Draw your eSignature here
          </div>
          <div className="text-[20px] md:text-[25px] text-gray-600 mt-[20px] max-w-[900px]">
            An online signature generator/maker is a tool that helps you create
            an online signature. eSignatures are a fast and easy way to sign
            contracts and legal documents.
          </div>
        </div>
        <div className={`flex flex-col justify-center items-center gap-[50px]`}>
          <div className=" bg-gray-50 border-[2px] rounded-xl">
            <SignatureCanvas
              penColor={penColor}
              maxWidth={penSize}
              ref={canvasRef}
              canvasProps={{
                width: screenWidth,
                height: 200,
                className: "sigCanvas",
              }}
            />
          </div>
          <div className="flex justify-center items-center text-center gap-[25px] sm:gap-[50px] text-gray-600">
            <div className="">
              <div className="">Color</div>
              <div className="flex items-center gap-[5px] border border-gray-400 py-[2px] px-[5px] pl-[15px]  relative rounded-full">
                <input
                  type="color"
                  value={penColor}
                  onChange={(e) => setPenColor(e.target.value)}
                  name=""
                  id=""
                  className="border-none outline-none rounded-full w-[25px] h-[30px]"
                />
                <input
                  type="text"
                  value={penColor}
                  onChange={(e) => setPenColor(e.target.value)}
                  maxLength="7"
                  name=""
                  id=""
                  className="w-[90px] border-none outline-none bg-transparent"
                />
              </div>
            </div>
            <div className="">
              <div className="">Pen Size</div>
              <input
                type="range"
                min="1"
                max="10"
                value={penSize}
                onChange={(e) => setPenSize(e.target.value)}
                name=""
                id=""
                className="w-[120px] sm:w-[200px] accent-violet-600 h-1 cursor-pointer range-sm border-transparent bg-gray-300"
              />
            </div>
            {/* <div className="">
                  <div className="">Angle</div>
                  <input type="range" name="" id="" className="w-[200px] accent-violet-600 h-1 cursor-pointer range-sm border-transparent bg-gray-300" />
               </div> */}
          </div>
          <div className="flex flex-col md:flex-row justify-center items-center gap-[30px]">
            <button
              onClick={() => clear()}
              className="flex justify-center items-center gap-[3px] bg-transparent text-violet-600 border border-violet-600 text-[18px] font-semibold py-[12px] px-[40px] rounded-full hover:opacity-80"
            >
              Clear and draw again
            </button>
            <button
              onClick={() => openModal()}
              className={`flex justify-center items-center gap-[3px] bg-violet-500 text-white text-[18px] font-semibold py-[12px] px-[40px] rounded-full hover:opacity-80`}
            >
              Download eSignature
            </button>
          </div>
        </div>

        {showModal && (
          <div
            className={`absolute z-[1000] top-0 left-0 w-full flex justify-center items-start px-[5px] py-[100px] bg-white/50 min-h-screen`}
          >
            <div className="relative flex flex-col md:flex-row justify-center items-center w-fit bg-violet-500 mx-auto rounded-2xl">
              <div
                onClick={() => setShowModal(false)}
                className="border border-b-none border-violet-300 shadow-xl absolute top-[-25px] left-[50%] -translate-x-[50%] w-[50px] h-[50px] hover:opacity-90 rounded-full cursor-pointer grid place-items-center bg-white text-violet-600 font-bold text-[20px]"
              >
                <AiOutlineClose className="font-bold" />
              </div>
              <div className="lg:w-[500px] md:h-[500px] lg:h-[400px] text-white flex flex-col justify-between gap-[50px] md:gap-[10px] p-[50px] px-[20px] lg:px-[50px]">
                <div className="flex flex-col gap-[15px]">
                  <div className="text-[25px] font-semibold">
                    Download Your Signature
                  </div>
                  <div className="text-[18px]">
                    Go artistic. Use your mouse or trackpad to make a statement.
                  </div>
                  <div className="w-full h-[100px] bg-white p-[10px] flex justify-center items-center rounded-lg relative">
                    {signatureUrl === "" ? (
                      <div className="text-[20px] text-gray-400">
                        Your signature will appear here
                      </div>
                    ) : (
                      <Image
                        src={signatureUrl}
                        width={100}
                        height={100}
                        className="w-[50%]"
                        alt=""
                      />
                    )}
                  </div>
                </div>
                <div className="">
                  <button
                    onClick={() => handleDownload()}
                    className="bg-white text-violet-600 text-[17px] md:text-[20px] px-[20px] py-[8px] md:px-[40px] md:py-[12px] rounded-full hover:opacity-80"
                  >
                    Download your signature
                  </button>
                </div>
              </div>
              <div className="w-[90%] h-[1px] md:w-[1px] md:h-[350px] bg-white"></div>
              <div className="lg:w-[500px] md:h-[500px] lg:h-[400px] text-white flex flex-col justify-between gap-[50px] md:gap-[10px] p-[50px] px-[20px] lg:px-[50px]">
                <div className="flex flex-col gap-[15px]">
                  <div className="text-[25px] font-semibold">
                    Take Signatures inside Forms!
                  </div>
                  <div className="text-[18px]">Get started for Free</div>
                  <div className="">
                    {[
                      "Integrated Signatures: Add digital signature fields to your forms for easy signing.",
                      "PDF Downloads: Download documents with signatures as PDF files.",
                      "Super easy and simple to use.",
                    ].map((item, index) => {
                      return (
                        <div key={index} className="flex gap-[10px] text-white">
                          <div className="">
                            <AiFillCheckCircle className="text-[20px]" />
                          </div>
                          {item}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="">
                  <Link
                    target="_blank"
                    href={`https://signature.boloforms.com/login?from=${
                      context.globalData.from || "ONLINE_SIGNATURES_TYPE_DRAW"
                    }${createUTMQueryParams({
                      utmData_: context.globalData.utmData,
                      from: "ONLINE_SIGNATURES_TYPE_DRAW",
                    })}`}
                    className="bg-white text-violet-600 text-[17px] md:text-[20px] px-[20px] py-[8px] md:px-[40px] md:py-[12px] rounded-full hover:opacity-80"
                  >
                    Check out BoloForms
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default DrawSignature;
